/**
 * Excel export, built in the browser: the base workbook at
 * public/production-calculator/ai-film-budget-template.xlsx carries the layout and live
 * formulas; we unzip it, write the inputs, the rate snapshot and the reopen
 * link into their cells, and zip it back. The cached values of every formula
 * cell are refreshed from compute() too, so previews that never recalculate
 * (Quick Look, mail attachments) still show the right numbers; Excel, Numbers
 * and Sheets recalculate on open (`fullCalcOnLoad`).
 */

import { strFromU8, strToU8, unzipSync, zipSync } from 'fflate'
import type { ShotBudgetRates } from '@/lib/marketing-config'
import { XLSX_ES } from './xlsxStrings'
import { compute, MODEL_VERSION, range, type BudgetParams } from './model'

export const BUDGET_TEMPLATE_PATH = '/production-calculator/ai-film-budget-template.xlsx'
const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'

/** Trims float noise (1.4000000000000001) before it lands in a cell. */
const num = (n: number) => String(Number(n.toPrecision(12)))

const escapeXml = (s: string) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')

/** Decode XML text before looking it up in the exact-key workbook catalog. */
export const decodeXml = (text: string): string =>
  text.replace(/&(#x[0-9a-f]+|#\d+|amp|lt|gt|quot|apos);/gi, (match, entity: string) => {
    if (entity.startsWith('#x')) return String.fromCodePoint(parseInt(entity.slice(2), 16))
    if (entity.startsWith('#')) return String.fromCodePoint(parseInt(entity.slice(1), 10))
    return ({ amp: '&', lt: '<', gt: '>', quot: '"', apos: "'" } as Record<string, string>)[entity] ?? match
  })

/** Matches one `<c r="REF" …>…</c>` (or self-closing) cell. The closing quote keeps B5 from matching B50. */
const cellRe = (ref: string) => new RegExp(`<c r="${ref}"([^>]*?)(?:/>|>([\\s\\S]*?)</c>)`)

/** Replaces a cell's content with a plain number, dropping any formula or string type. */
function setNumber(xml: string, ref: string, value: number): string {
  return xml.replace(cellRe(ref), (_m, attrs: string) => {
    const kept = attrs.replace(/\s+t="\w+"/, '')
    return `<c r="${ref}"${kept} t="n"><v>${num(value)}</v></c>`
  })
}

/** Replaces a cell's content with an inline string (no shared-strings table edit needed). */
function setString(xml: string, ref: string, value: string): string {
  return xml.replace(cellRe(ref), (_m, attrs: string) => {
    const kept = attrs.replace(/\s+t="\w+"/, '')
    return `<c r="${ref}"${kept} t="inlineStr"><is><t>${escapeXml(value)}</t></is></c>`
  })
}

/** Refreshes the cached value of a formula cell; the formula itself is untouched. */
function setCached(xml: string, ref: string, value: number | null): string {
  return xml.replace(cellRe(ref), (m: string) => {
    // null: the formula yields "" (e.g. per-minute with no AI minutes), so cache an empty string cell.
    const cached = value === null ? '<v></v>' : `<v>${num(value)}</v>`
    // The type attribute must follow `r="…"`: every cell lookup here keys on `<c r=`.
    const typed = value === null ? m.replace(/\st="\w+"/, '').replace(/^<c r="([A-Z]+\d+)"/, '<c r="$1" t="str"') : m
    return typed.includes('<v>') ? typed.replace(/<v>[\s\S]*?<\/v>/, cached) : typed.replace('</c>', `${cached}</c>`)
  })
}

export interface FillOptions {
  locale?: string
  params: BudgetParams
  rates: ShotBudgetRates
  /** The exact /production-calculator URL that reopens this budget. */
  reopenUrl: string
  /** Export date, already formatted. */
  exportedOn: string
}

/** Pure: template bytes in, filled workbook bytes out. */
export function fillBudgetWorkbook(
  template: Uint8Array,
  { params: p, rates, reopenUrl, exportedOn, locale = 'en' }: FillOptions,
): Uint8Array {
  const files = unzipSync(template)
  const edit = (path: string, fn: (xml: string) => string) => {
    const file = files[path]
    if (!file) throw new Error(`Budget template is missing ${path}`)
    files[path] = strToU8(fn(strFromU8(file)))
  }
  // i18n-ignore — these English template keys are localized through the Excel catalog.
  const text = (key: string, values: Record<string, string> = {}) => {
    if (locale === 'es' && !Object.hasOwn(XLSX_ES, key)) {
      throw new Error(`Missing Spanish workbook translation: ${key}`)
    }
    const template = locale === 'es' ? XLSX_ES[key] : key
    return template.replace(/\{(\w+)\}/g, (match, name: string) => values[name] ?? match)
  }
  if (locale === 'es') {
    edit('xl/sharedStrings.xml', xml =>
      xml.replace(
        /(<t\b[^>]*>)([\s\S]*?)(<\/t>)/g,
        (_match, open: string, value: string, close: string) => `${open}${escapeXml(text(decodeXml(value)))}${close}`,
      ),
    )
    edit('xl/workbook.xml', xml =>
      xml.replace(
        /(<sheet\b[^>]*\bname=")([^"]+)(")/g,
        (_match, open: string, name: string, close: string) => `${open}${escapeXml(text(decodeXml(name)))}${close}`,
      ),
    )
    // Always quote sheet names; double apostrophes inside Excel identifiers.
    const ratesSheet = "'" + text('Rates').replace(/'/g, "''") + "'!"
    for (const path of Object.keys(files).filter(path => /^xl\/worksheets\/[^/]+\.xml$/.test(path))) {
      edit(path, xml =>
        xml
          .replace(
            /(<f\b[^>]*>)([\s\S]*?)(<\/f>)/g,
            (_match, open: string, formula: string, close: string) =>
              `${open}${formula.replace(/Rates!/g, escapeXml(ratesSheet))}${close}`,
          )
          // A few template cells carry inline strings instead of shared ones.
          .replace(
            /(<is><t\b[^>]*>)([\s\S]*?)(<\/t><\/is>)/g,
            (_match, open: string, value: string, close: string) =>
              `${open}${escapeXml(text(decodeXml(value)))}${close}`,
          ),
      )
    }
  }
  const m = compute(p, rates)
  const { lo, hi } = range(p, rates)

  edit('xl/worksheets/sheet1.xml', xml => {
    const inputs: [string, number][] = [
      ['B5', p.runtime],
      ['B6', p.ai / 100],
      ['B7', p.clipLen],
      ['B8', p.shotLen],
      ['B9', p.tries],
      ['B10', p.draftShare / 100],
      ['B11', p.imgs],
      ['B12', p.hdr ? 1 : 0],
      ['B14', p.gensPerDay],
      ['B15', p.artistRate],
      ['B16', p.team],
      ['B17', p.cleanShare / 100],
      ['B18', p.cleanHours],
      ['B19', p.cleanRate],
      ['B20', p.prepDays],
    ]
    for (const [ref, v] of inputs) xml = setNumber(xml, ref, v)
    // The template's rate lookup matches "720" / "1080" / "4K" exactly — not the on-page "1080p" label.
    xml = setString(xml, 'B22', p.res === '4k' ? '4K' : p.res)
    const cached: [string, number | null][] = [
      ['B13', rates.hdrPerSecondUsd[p.res]],
      ['B23', rates.perSecondUsd.draft],
      ['B24', rates.perSecondUsd[p.res]],
      ['B25', rates.stillUsd],
      ['B26', rates.oliveUsd],
      ['B29', m.finSec],
      ['B30', m.keepers],
      ['B31', m.takes],
      ['B32', m.drafts],
      ['B33', m.finals],
      ['B34', m.draftEach],
      ['B35', m.finalEach],
      ['B36', m.perKeeper],
      ['B37', m.keepers * m.perKeeper],
      ['B38', m.stills],
      ['B39', m.hdr],
      ['B40', m.gen],
      ['B41', m.artistDays],
      ['B42', m.months],
      ['B43', m.art],
      ['B44', m.flagged],
      ['B45', m.perCleanup],
      ['B46', m.cln],
      ['B49', m.gen],
      ['B50', m.art],
      ['B51', m.cln],
      ['B52', m.total],
      ['B53', m.aiMin > 0 ? m.total / m.aiMin : null],
      ['B54', m.gen / rates.oliveUsd],
      ['B55', (p.tries * p.clipLen) / m.used],
      ['B56', m.total ? m.gen / m.total : 0],
      ['B57', m.total ? m.art / m.total : 0],
      ['B58', m.total ? m.cln / m.total : 0],
      ['B61', lo],
      ['B62', hi],
    ]
    for (const [ref, v] of cached) xml = setCached(xml, ref, v)
    return xml
  })

  edit('xl/worksheets/sheet2.xml', xml => {
    xml = setString(xml, 'A3', text('As of: {date}', { date: exportedOn }))
    const cells: [string, number][] = [
      ['C5', rates.perSecondUsd.draft],
      ['C6', rates.perSecondUsd['720']],
      ['C7', rates.perSecondUsd['1080']],
      ['C8', rates.perSecondUsd['4k']],
      ['C9', rates.stillUsd],
      ['C10', rates.oliveUsd],
      ['C11', rates.hdrPerSecondUsd['1080']],
      ['C12', rates.hdrPerSecondUsd['4k']],
    ]
    for (const [ref, v] of cells) xml = setNumber(xml, ref, v)
    return xml
  })

  edit('xl/worksheets/sheet3.xml', xml => {
    xml = setString(
      xml,
      'A2',
      text('Model version: v{version} · Rates as of: {date}', { version: String(MODEL_VERSION), date: exportedOn }),
    )
    xml = setString(xml, 'A3', text('Reopen online: {url}', { url: reopenUrl }))
    return xml
  })

  return zipSync(files, { level: 6 })
}

export function budgetXlsxFilename(p: BudgetParams, date = new Date(), locale = 'en'): string {
  return `${locale === 'es' ? 'Presupuesto-Cine-IA' : 'AI-Film-Budget'}_${p.runtime}min_${date.toISOString().slice(0, 10)}.xlsx`
}

/** Fetches the template, fills it, and hands the browser a download. */
export async function downloadBudgetXlsx(options: FillOptions): Promise<void> {
  const res = await fetch(BUDGET_TEMPLATE_PATH)
  if (!res.ok) {
    // Operator copy only; the status rides on the instance for Sentry, never in the message.
    throw Object.assign(new Error('Budget template fetch failed'), { status: res.status })
  }
  const bytes = fillBudgetWorkbook(new Uint8Array(await res.arrayBuffer()), options)
  const blob = new Blob([bytes as BlobPart], { type: XLSX_MIME })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = budgetXlsxFilename(options.params, new Date(), options.locale)
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}
